import { cn } from '@/lib/utils'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import React, { useState, type CSSProperties } from 'react'
import {
  AccordionItemLink,
  type AccordionItemLinkProps,
} from './accordion-item-link'

export type AccordionSectionProps = {
  action?: React.ReactNode
  badgeStyle?: CSSProperties
  chevronStyle?: CSSProperties
  className?: string
  /**
   * When false the section renders as a static banner: no chevron, not
   * clickable and the `items` list is never shown. Defaults to true.
   */
  collapsible?: boolean
  contentClassName?: string
  count?: number
  defaultOpen?: boolean
  description?: React.ReactNode
  descriptionStyle?: CSSProperties
  headerStyle?: CSSProperties
  icon?: React.ReactNode
  id: string
  items?: AccordionItemLinkProps[]
  style?: CSSProperties
  title: string
  titleStyle?: CSSProperties
}

export const AccordionSection = ({
  action,
  badgeStyle,
  chevronStyle,
  className,
  collapsible = true,
  contentClassName,
  count,
  defaultOpen = false,
  description,
  descriptionStyle,
  headerStyle,
  icon,
  items = [],
  style,
  title,
  titleStyle,
}: AccordionSectionProps) => {
  const [open, setOpen] = useState(defaultOpen)

  const header = (
    <>
      <div className="flex min-w-0 items-center gap-2">
        {icon}
        <div className="min-w-0">
          <p className="min-w-0 text-[14px] font-semibold" style={titleStyle}>
            {title}
          </p>
          {description ? (
            <p className="mt-0.5 text-[13px]" style={descriptionStyle}>
              {description}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {action}
        {count != null ? (
          <span
            className="inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-[11px] font-semibold"
            style={badgeStyle}
          >
            {count}
          </span>
        ) : null}
        {collapsible ? (
          open ? (
            <ChevronUpIcon className="size-4" style={chevronStyle} />
          ) : (
            <ChevronDownIcon className="size-4" style={chevronStyle} />
          )
        ) : null}
      </div>
    </>
  )

  return (
    <div
      className={cn('overflow-hidden rounded-[14px] border', className)}
      style={style}
    >
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen(current => !current)}
          className="flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-3 text-left"
          style={headerStyle}
        >
          {header}
        </button>
      ) : (
        <div
          className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left"
          style={headerStyle}
        >
          {header}
        </div>
      )}

      {collapsible && open && items.length > 0 && (
        <div className={cn('space-y-0 bg-white', contentClassName)}>
          {items.map(item => (
            <AccordionItemLink key={item.itemKey} {...item} />
          ))}
        </div>
      )}
    </div>
  )
}
